import { DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined, CheckOutlined } from '@ant-design/icons'
import { Button, Card, Input, Modal, Select, Table } from 'antd'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FraudDomainBean, ItemResponseFraudDomainBeanObject, ListPagination } from 'src/api/api'
import { useDebounce } from 'src/util/useDebounce'
import fraudDomainApi from './FraudDomainApi'
import { StorageKey, memoryStorage, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { toSort, useSorter } from 'src/util/useSorter'
import { enumView } from 'src/util/Util'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('fraudDomain', memoryStorage)
const storageReader = storageReaderFactory('fraudDomain', memoryStorage)

interface Props { }

const FraudDomainList: React.FC<Props> = () => {

    const [data, setData] = useState<FraudDomainBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const savedParams = storageReader.read(StorageKey.SearchParams)
    const isInitialLoad = useRef(true)

    const [blockingReason, setBlockingReason] = useState<string | undefined>(savedParams.blockingReason ?? undefined)

    const remove = (record: FraudDomainBean) => {
        Modal.confirm({
            title: `Delete ${record.domain}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                await fraudDomainApi.delete(record.id as number)
                let page = pagination.current
                if (data.length == 1 && pagination.current > 1) {
                    page -= 1
                }
                loadData(page)
            }
        })
    }

    const activate = async (record: FraudDomainBean, index) => {
        await fraudDomainApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const loadData = async (page: number) => {

        const response = await fraudDomainApi.list(pagination.defaultPageSize, page, toSort(sorter), search, blockingReason)

        const array = response.data as Array<ItemResponseFraudDomainBeanObject>
        const beans: FraudDomainBean[] = array.map(item => item.data as FraudDomainBean)

        if (beans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage,
                blockingReason
            })

            setData(beans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const handleBlockingReasonChange = (value: string | undefined) => {
        setBlockingReason(value)
        storageWriter.write(StorageKey.SearchParams, { blockingReason: value ?? null })
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false
            loadData(savedParams.page || pagination.current)
        } else {
            loadData(1)
        }
    }, [debouncedSearch, blockingReason, sorter])

    return <Card
        title="Fraud Domains"
        className="list-card"
    >
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by Domain" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Blocking Reason" value={blockingReason} onChange={handleBlockingReasonChange} allowClear>
                {Object.values(FraudDomainBean.BlockingReasonEnum).map(reason => <Option key={reason} value={reason}>{enumView(reason)}</Option>)}
            </Select>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column key="id" title="ID" dataIndex="id" />
            <Column key="domain" title="Domain" dataIndex="domain" render={(text, record: FraudDomainBean) => <Link to={`/fraud-domain/${record.id}/edit`}>{text}</Link>} />
            <Column key="blockingReason" title="Blocking Reason" dataIndex="blockingReason" render={text => enumView(text)} />
            <Column key="ctr" title="CTR" dataIndex="ctr" />
            <Column key="modified" title="Last Modified" dataIndex="modified" />
            <Column align="center" key="action" title="Actions" render={(text: string, record: FraudDomainBean, index) =>
                <span>
                    <Button type="text" danger={record.active} onClick={() => activate(record, index)}>
                        <CheckOutlined />
                    </Button>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            } />
        </Table>
    </Card>
}

export default FraudDomainList



// WEBPACK FOOTER //
// ./src/component/app/fraudDomain/FraudDomainList.tsx